/**
 * User preference storage for portal2.
 * This is the second (and currently only other) module permitted to touch
 * localStorage besides tokenStore. ESLint enforces the boundary — see
 * eslint.config.js no-restricted-syntax + the override block below.
 * Scope: UI preferences that should survive a reload but are not sensitive
 * (theme, density, language, last-visited route, etc.). Anything secret
 * still belongs in tokenStore (in-memory) or in an HttpOnly cookie.
 */

const STORAGE_KEY = 'om.prefs';

/** Theme key drives Tailwind's class-based dark mode (.dark on <html>). */
export type Theme = 'dark' | 'light';

/** Supported i18n languages. Kept in sync with `app/providers/i18n.ts`. */
export type Language = 'en' | 'fr' | 'es' | 'pt';

/** A recently-picked SKU card in the AI product-matching picker's "Recent" tab. */
export interface RecentPick {
  sku: string;
  title: string;
  category: string;
  attrs: string[];
}

export interface PortalPrefs {
  theme: Theme;
  language: Language | null;
  /** Whether the main nav rail is collapsed to an icon-only strip. */
  sidebarCollapsed: boolean;
  /** Heading keys of nav sections the user has collapsed (e.g. 'admin'). */
  collapsedNavSections: string[];
  /**
   * Last-applied Orders list query string (filters/search/sort/page), minus the
   * leading `?`. Lets the filters survive navigating away and back, matching the
   * legacy orders-v2 Redux slice.
   */
  ordersSearch: string;
  /**
   * Last-applied Shipments list query string (filters/search/sort/page), minus
   * the leading `?`. Lets the vendor shipments filters survive opening a
   * shipment detail or another section and returning.
   */
  shipmentsSearch: string;
  /**
   * AI product-matching picker's recent SKU picks (most-recent-first, capped). The
   * dedupe/cap/normalize logic lives in the feature's recentPicks module; this just
   * persists the list.
   */
  productMatchRecentPicks: RecentPick[];
}

const defaults: PortalPrefs = {
  theme: 'dark',
  language: null,
  sidebarCollapsed: false,
  collapsedNavSections: [],
  ordersSearch: '',
  shipmentsSearch: '',
  productMatchRecentPicks: [],
};

type Listener = (prefs: PortalPrefs) => void;
const listeners = new Set<Listener>;

function readRaw(): Partial<PortalPrefs> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Partial<PortalPrefs>;
  } catch {
    return {};
  }
}

function writeRaw(prefs: PortalPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // storage unavailable — preferences remain in-memory for this session.
  }
}

function isTheme(value: unknown): value is Theme {
  return value === 'dark' || value === 'light';
}

function isLanguage(value: unknown): value is Language {
  return value === 'en' || value === 'fr' || value === 'es' || value === 'pt';
}

function isRecentPick(value: unknown): value is RecentPick {
  if (!value || typeof value !== 'object') return false;
  const p = value as Record<string, unknown>;
  return (
    typeof p.sku === 'string' &&
    typeof p.title === 'string' &&
    typeof p.category === 'string' &&
    Array.isArray(p.attrs) &&
    p.attrs.every((a) => typeof a === 'string')
  );
}

let current: PortalPrefs = (() => {
  const raw = readRaw();
  return {
    theme: isTheme(raw.theme) ? raw.theme : defaults.theme,
    language: isLanguage(raw.language) ? raw.language : defaults.language,
    sidebarCollapsed:
      typeof raw.sidebarCollapsed === 'boolean' ? raw.sidebarCollapsed : defaults.sidebarCollapsed,
    collapsedNavSections: Array.isArray(raw.collapsedNavSections)
      ? raw.collapsedNavSections.filter((s): s is string => typeof s === 'string')
      : defaults.collapsedNavSections,
    ordersSearch: typeof raw.ordersSearch === 'string' ? raw.ordersSearch : defaults.ordersSearch,
    shipmentsSearch:
      typeof raw.shipmentsSearch === 'string' ? raw.shipmentsSearch : defaults.shipmentsSearch,
    productMatchRecentPicks: Array.isArray(raw.productMatchRecentPicks)
      ? raw.productMatchRecentPicks.filter(isRecentPick)
      : defaults.productMatchRecentPicks,
  };
})();

function notify(): void {
  for (const listener of listeners) listener(current);
}

export const prefsStore = {
  get(): PortalPrefs {
    return current;
  },

  setTheme(theme: Theme): void {
    if (current.theme === theme) return;
    current = {...current, theme };
    writeRaw(current);
    notify();
  },

  toggleTheme(): void {
    this.setTheme(current.theme === 'dark' ? 'light' : 'dark');
  },

  setLanguage(language: Language): void {
    if (current.language === language) return;
    current = {...current, language };
    writeRaw(current);
    notify();
  },

  setSidebarCollapsed(collapsed: boolean): void {
    if (current.sidebarCollapsed === collapsed) return;
    current = {...current, sidebarCollapsed: collapsed };
    writeRaw(current);
    notify();
  },

  toggleSidebar(): void {
    this.setSidebarCollapsed(!current.sidebarCollapsed);
  },

  /** Persist the last-applied Orders list query string (see PortalPrefs). */
  setOrdersSearch(search: string): void {
    if (current.ordersSearch === search) return;
    current = {...current, ordersSearch: search };
    writeRaw(current);
    notify();
  },

  /** Persist the last-applied Shipments list query string (see PortalPrefs). */
  setShipmentsSearch(search: string): void {
    if (current.shipmentsSearch === search) return;
    current = {...current, shipmentsSearch: search };
    writeRaw(current);
    notify();
  },

  /** Persist the AI product-matching picker's recent SKU picks (see PortalPrefs). */
  setProductMatchRecentPicks(picks: RecentPick[]): void {
    current = { ...current, productMatchRecentPicks: picks };
    writeRaw(current);
    notify();
  },

  /**
   * Drop the persisted list query strings (Orders + Shipments). Called on a
   * real sign-in (password login / OAuth code exchange) so a fresh session
   * starts with clean filters instead of inheriting the previous session's —
   * or a previous user's — stuck filters. UI preferences
   * (theme / language / sidebar) intentionally survive sign-in. The
   * session-rehydrate path (AuthBootstrap on F5) must NOT call this: filters
   * surviving a refresh within a session is the intended behaviour.
   */
  clearSearches(): void {
    if (!current.ordersSearch && !current.shipmentsSearch) return;
    current = { ...current, ordersSearch: '', shipmentsSearch: '' };
    writeRaw(current);
    notify();
  },

  /** Collapse/expand a nav section by its heading key. */
  toggleNavSection(key: string): void {
    const next = new Set(current.collapsedNavSections);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    current = {...current, collapsedNavSections: [...next] };
    writeRaw(current);
    notify();
  },

  reset(): void {
    current = {...defaults };
    writeRaw(current);
    notify();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
